import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { Flex, Spinner, Textarea } from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useUserEditorProvider } from '../../../providers/UserEditor/userEditorProvider';
import { useUserProvider } from '../../../providers/User/userProvider';
import { t } from 'i18next';
import { PenIcon } from '../../../utils/icons/pen';
import { scrollColoredSmall } from '../../../utils/themer';
import { Color } from '../../../theme/color';

enum RESULT_TYPES {
	IS_EDITING,
	START_EDIT,
	NOT_AN_OWNER,
	LOADING,
}

export const UserDescription = ({ isOwner }: { isOwner: boolean }) => {
	const { data: userData } = useUserProvider();
	const editorUserData = useUserEditorProvider();

	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [wasSaved, setWasSaved] = useState(true);

	const textAreaReference = useRef(null) as React.MutableRefObject<null> as any;

	const editDescription = () => {
		setIsEditing(true);
		textAreaReference.current.focus();
		textAreaReference.current.selectionStart = textAreaReference.current.selectionEnd =
			textAreaReference.current.value.length;
	};

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const { value } = event.target;

		editorUserData.setDescription(value);

		if (value === userData.description) {
			setWasSaved(true);
		} else {
			setWasSaved(false);
		}
	};

	const handleClose = () => {
		setIsEditing(false);
	};

	const handleApprove = () => {
		setWasSaved(true);
		handleClose();
	};

	const onCancelEditing = () => {
		editorUserData.setDescription(null);
		handleClose();
	};

	const handleBlur = () => {
		if (wasSaved) {
			handleClose();
		}
	};

	const resultType = useMemo(() => {
		if (!isOwner) {
			return RESULT_TYPES.NOT_AN_OWNER;
		}
		if (isOwner && !isEditing) {
			return RESULT_TYPES.START_EDIT;
		}
		if (isOwner && isEditing) {
			return RESULT_TYPES.IS_EDITING;
		}
		return RESULT_TYPES.LOADING;
	}, [isOwner, isEditing]);

	const typeOptions = {
		[RESULT_TYPES.NOT_AN_OWNER]: () => null,
		[RESULT_TYPES.START_EDIT]: () => (
			<Flex
				display={{ base: 'none', hashup: 'flex' }}
				cursor="pointer"
				onClick={editDescription}
				ml="6px"
				borderRadius="5px"
				justifyContent="center"
				alignItems="center"
				border={`1px solid ${Color.GRAY_BORDER_LIGHT}`}
				borderColor="white"
				bg={Color.BACKGROUND}
				w="26px"
				h="26px"
			>
				<PenIcon />
			</Flex>
		),
		[RESULT_TYPES.IS_EDITING]: () =>
			isLoading ? (
				<Spinner
					maxW="22px"
					maxH="22px"
				/>
			) : (
				<Flex
					ml="5px"
					flexDirection="column"
					gridGap="10px"
				>
					<CheckIcon
						maxW="22px"
						maxH="22px"
						cursor="pointer"
						onClick={handleApprove}
					/>
					<CloseIcon
						maxW="22px"
						maxH="22px"
						color={Color.ACTION}
						cursor="pointer"
						onClick={onCancelEditing}
					/>
				</Flex>
			),
		[RESULT_TYPES.LOADING]: () => <Spinner size="sm" />,
	};

	return (
		<Flex
			bg="#11111180"
			w="100%"
			justifyContent="flex-end"
			flexDirection="column"
			p="25px"
			pos="relative"
			border="1px solid #ffffff40"
			borderRadius="5px"
			_after={{
				width: '12px',
				height: '3px',
				backgroundColor: editorUserData.color ?? userData.color,
				position: 'absolute',
				top: '-1px',
				left: '29px',
				content: '""',
			}}
		>
			<Flex>
				<Textarea
					css={scrollColoredSmall(
						Color.BACKGROUND_LIGHT,
						editorUserData.color ?? userData.color ?? Color.BACKGROUND_LIGHT
					)}
					p="0"
					_focus={{ boxShadow: 'none' }}
					onChange={handleChange}
					onBlur={handleBlur}
					isDisabled={!isEditing || isLoading}
					_disabled={{ color: 'white' }}
					fontSize={{ base: '10px', hashup: '14px' }}
					fontWeight={{ base: '300', hashup: '400' }}
					lineHeight={{ base: '16px', hashup: '22px' }}
					letterSpacing={{ base: 'unset', hashup: '.02em' }}
					resize="none"
					border="none"
					h="100%"
					maxLength={600}
					value={editorUserData.description ?? userData.description}
					disabled={!isOwner}
					onClick={editDescription}
					ref={textAreaReference}
					placeholder={`${editorUserData.nickname ?? (userData.nickname || 'Unnamed User')}${t(
						'user.descriptionSuffix'
					).toString()}`}
					_placeholder={{ color: '#ffffff80' }}
				/>
				{typeOptions[resultType]()}
			</Flex>
		</Flex>
	);
};
